import { t } from 'i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import {
  iconBrush,
  iconBullet,
  iconMinus,
  iconPlus,
  iconProductImage,
} from '@/components/icons';

import { baseStyles, typographyStyles } from '@/styles';
import './fg-personalisation-file';

@customElement('fg-personalisation-choose-visual-designer')
export class FgPersonalisationChooseVisualDesigner extends LitElement {
  render() {
    const uspsVisualDesigner = [
      'content.personalisation.visualDesignerStep.options.visualDesigner.usps.usp1',
      'content.personalisation.visualDesignerStep.options.visualDesigner.usps.usp2',
      'content.personalisation.visualDesignerStep.options.visualDesigner.usps.usp3',
      'content.personalisation.visualDesignerStep.options.visualDesigner.usps.usp4',
    ];
    const uspsManual = [
      'content.personalisation.visualDesignerStep.options.manual.usps.usp1',
      'content.personalisation.visualDesignerStep.options.manual.usps.usp2',
      'content.personalisation.visualDesignerStep.options.manual.usps.usp3',
    ];

    return html`
      <div class="cards">
        <div class="wrapper">
          <button
            class="card highlight"
            @click=${() => {
              this.dispatchEvent(
                new CustomEvent('select-method', {
                  detail: {
                    visualDesigner: true,
                  },
                }),
              );
              this.dispatchEvent(
                new CustomEvent('handle-announcement', {
                  detail: {
                    announcement: t(
                      `content.personalisation.visualDesignerStep.options.visualDesigner.ariaSelectedTitle`,
                    ),
                  },
                }),
              );
            }}
          >
            <span class="label" aria-hidden="true">${t('labels.new')}</span>
            <span class="icon" aria-hidden="true">${iconBrush}</span>
            <div class="content">
              <header>
                <h2>
                  ${t(
                    'content.personalisation.visualDesignerStep.options.visualDesigner.title',
                  )}
                </h2>
              </header>
              <ul>
                ${uspsVisualDesigner.map(usp => this.renderUsp(usp))}
              </ul>
            </div>
          </button>
          <footer>${t('labels.quickAndEasy')}</footer>
        </div>
        <div class="wrapper">
          <button
            class="card"
            @click=${() => {
              this.dispatchEvent(
                new CustomEvent('select-method', {
                  detail: {
                    url: false,
                  },
                }),
              );
              this.dispatchEvent(
                new CustomEvent('handle-announcement', {
                  detail: {
                    announcement: t(
                      `content.personalisation.visualDesignerStep.options.manual.ariaSelectedTitle`,
                    ),
                  },
                }),
              );
            }}
          >
            <span class="icon" aria-hidden="true">${iconProductImage}</span>
            <div class="content">
              <header>
                <h2>
                  ${t(
                    'content.personalisation.visualDesignerStep.options.manual.title',
                  )}
                </h2>
              </header>
              <ul>
                ${uspsManual.map(usp => this.renderUsp(usp))}
              </ul>
            </div>
          </button>
        </div>
      </div>
    `;
  }
  private renderUsp(usp: string) {
    const text = t(usp);
    const tokenMap: Record<
      string,
      { icon: typeof iconPlus; className: string }
    > = {
      __PLUS__: { icon: iconPlus, className: 'pro' },
      __MIN__: { icon: iconMinus, className: 'con' },
      __BULLET__: { icon: iconBullet, className: '' },
    };
    const token = Object.keys(tokenMap).find(key => text.startsWith(key));
    const match = token ? tokenMap[token] : undefined;
    const label = token ? text.substring(token.length).trimStart() : text;

    return html`
      <li>
        ${match
          ? html`
              <span class="procon-icon ${match.className}" aria-hidden="true">
                ${match.icon}
              </span>
            `
          : nothing}
        ${label}
      </li>
    `;
  }

  static styles = [
    baseStyles,
    typographyStyles,
    css`
      :host([part='wholesale']) h2 {
        font-size: var(--fg-font-size--2xl, var(--fg-fallback-font-size--2xl));
      }

      .cards {
        display: grid;
        gap: 4px;
        grid-template-columns: 1fr 1fr;
      }
      .wrapper {
        position: relative;
        z-index: 1;
      }
      .card {
        height: calc(100% - 44px);
      }
      button {
        background: white;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        display: flex;
        flex-direction: column;
        margin-bottom: 44px;
        padding-top: 45px;
        position: relative;
        width: 100%;
      }
      :host([part='retail']) button.card.highlight:not(:focus) {
        outline: 3px solid var(--fg-primary, var(--fg-fallback-primary));
      }
      button.card.highlight:not(:focus) {
        outline: 3px solid var(--fg-secondary, var(--fg-fallback-secondary));
        outline-offset: -3px;
      }
      button:hover,
      button:focus {
        border-color: var(--fg-primary, var(--fg-fallback-primary));
        box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
        cursor: pointer;
      }
      .label {
        background-color: var(--fg-cta, var(--fg-fallback-cta));
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        color: white;
        display: block;
        font-weight: bold;
        left: 8px;
        padding: 4px 12px;
        position: absolute;
        text-transform: capitalize;
        top: 8px;
        width: fit-content;
      }
      :host([part='retail']) .icon {
        color: var(--fg-primary, var(--fg-fallback-primary));
      }
      .icon {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        display: grid;
        height: 64px;
        place-content: center;
        text-align: center;
        width: 100%;
      }
      .content {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        justify-content: start;
        padding: 0 16px 8px;
      }
      :host([part='retail']) h2,
      :host([part='wholesale']) h2 {
        margin: 24px 0 8px 0;
      }
      p {
        margin: 0;
        padding: 4px 0 0 0;
      }
      ul {
        list-style-type: none;
        margin-left: -4px;
        margin-top: 0;
        padding: 0;
      }
      li {
        display: flex;
        margin: 6px 0;
      }
      li span {
        flex-shrink: 0;
        margin-right: 3px;
      }
      .procon-icon {
        display: grid;
        height: 16px;
        margin-top: 3px;
        place-content: center;
        width: 16px;
      }
      .procon-icon.pro {
        color: var(--fg-success--dark, var(--fg-fallback-success--dark));
      }
      .procon-icon.con {
        color: var(--fg-red--500, var(--fg-fallback-red--500));
      }
      :host([part='retail']) footer {
        background: var(--fg-primary, var(--fg-fallback-primary));
      }
      footer {
        background: var(--fg-secondary, var(--fg-fallback-secondary));
        color: white;
        display: grid;
        border-bottom-left-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        border-bottom-right-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        bottom: 6px;
        font-size: 1.125rem;
        height: 44px;
        padding-top: 3px;
        place-content: center;
        position: absolute;
        text-align: center;
        width: 100%;
        z-index: -1;
      }
    `,
  ];
}
